import { defineStore } from 'pinia'
import { mscwApi } from '@/helpers/api'

export const useClosedOnlineTestStore = defineStore('ClosedOnlineTestStore', {
  state: () => ({
    isLoggedIn: null,
    isAllowedCountry: null,
    isSignedUp: false,
    isEmailSubscribed: false,
    hideBtnActions: false, // for hiding the actions after showing not allowed dialog
    showSignedUpAndSubscribedText: false,
    showRequestSubmittedText: false,
    mscwEventNo: '103',
    dialog: {
      show: false,
      type: 'signup-disclaimer', //signup-disclaimer, signup-confirmed, email-confirmed, not-eligible, error
    },
  }),
  getters: {
    dialogTitle() {
      switch (this.dialog.type) {
        case 'signup-disclaimer':
          return 'Note'
        case 'signup-confirmed':
          return 'Sign-up Confirmed'
        case 'email-confirmed':
          return 'Email Confirmed'
        case 'not-eligible':
          return 'Not Eligible'
        case 'error':
          return 'Error'
        default:
          return ''
      }
    },

    dialogDescription() {
      switch (this.dialog.type) {
        case 'signup-disclaimer':
          return '' // it's the checkbox in the dialog component
        case 'signup-confirmed':
          return `Selected players will be contacted via email. <br/>You may also receive an emailed survey <br/>after the online test ends.`
        case 'email-confirmed':
          return 'You have successfully signed up to receive updates!'
        case 'not-eligible':
          return `Event sign-up is only for players living in <br/>the Global MapleStory service region.`
        case 'error':
          return 'An error has occurred. Please try again later.'
        default:
          return ''
      }
    },

    dialogBtnText() {
      switch (this.dialog.type) {
        case 'signup-disclaimer':
          return 'Next'
        case 'signup-confirmed':
          return 'Done'
        case 'email-confirmed':
          return 'Done'

        default:
          return 'OK'
      }
    },

    hideHeroActions() {
      return this.hideBtnActions || (this.isSignedUp && this.isEmailSubscribed)
    },
  },
  actions: {
    login() {
      const returnUrl = new window.URL(window.location.href)
      returnUrl.searchParams.set('cwcotsignup', 'true')
      window.nexon.sso.login(returnUrl.toString())
    },

    async checkUserIsAllowed() {
      try {
        await this.checkUserEventStatus()
        this.isLoggedIn = true
        this.isAllowedCountry = true
      } catch (e) {
        if (e && e.response) {
          const errorCode = e.response.headers?.['x-arena-web-errorcode']

          if (e.response.status === 401) {
            // user is not logged in and we don't know if it's allowed country
            this.isLoggedIn = false
            this.isAllowedCountry = null
          } else if (e.response.status === 403 && errorCode === '22002') {
            // user is logged in but not allowed country
            this.isLoggedIn = true
            this.isAllowedCountry = false
          }
        } else {
          throw e
        }
      }
    },

    async checkUserEventStatus() {
      try {
        const { data } = await mscwApi.get(`/events/${this.mscwEventNo}/user?fields=gameEmailOptIn`)
        this.isSignedUp = true // if request is successful, user is signed up
        this.isEmailSubscribed = data.gameEmailOptIn

        if (this.isSignedUp && this.isEmailSubscribed) {
          this.showRequestSubmittedText = true
          this.showSignedUpAndSubscribedText = true
        }
      } catch (e) {
        if (e && e.response && e.response.status === 400) {
          // user didn't sign up yet
          this.dialog.type = 'signup-disclaimer'
        } else {
          this.dialog.type = 'error'
          throw e
        }
      }
    },

    async signUp() {
      try {
        await mscwApi.post(`/events/${this.mscwEventNo}/user`)
        await this.checkUserEventStatus()
        this.dialog.type = 'signup-confirmed'
      } catch (e) {
        const webErrorCode = e?.response?.headers?.['x-arena-web-errorcode']
        // 22031: EVENT_USER_ALREADY_EXISTS - treat an already-signed-up user as confirmed
        if (webErrorCode === '22031') {
          this.isSignedUp = true
          this.dialog.type = 'signup-confirmed'
        } else {
          this.dialog.type = 'error'
        }
      }
    },

    async emailSubscribe() {
      try {
        await mscwApi.post(`/events/${this.mscwEventNo}/email-opt-in`)
        this.dialog.type = 'email-confirmed'
        this.dialog.show = true
        this.isEmailSubscribed = true
        this.showRequestSubmittedText = true
        this.showSignedUpAndSubscribedText = true // after user is signed up and subscribed (only first time, don't need on refresh page based on wireframe) show signed up & subscribed
      } catch {
        this.dialog.type = 'error'
        this.dialog.show = true
      }
    },
  },
})
